// web/src/components/enroll/InitiatorFlow.tsx — the INITIATOR lobby → seal →
// receipt flow (wireframe 04; ADR-003 D1/D3; SEAL dynamic initiator side).
//
//   1. Open a session (POST /enroll) → show the join code (what3words + PIN).
//   2. Poll the live roster; per joiner show email + auth method + safety number.
//   3. ADMIT after an out-of-band fingerprint check, or REJECT (the human vouches
//      the channel; the server re-verifies the signed attestation — D4).
//   4. When N members are admitted, run the seal CLIENT-SIDE: buildSealRequest
//      (envelope + Shamir-split data key + one ECDH WireShard per member) then
//      POST /records. The plaintext NEVER leaves the browser (R11).
//
// The plaintext draft is carried in-memory from the new-secret wizard via the
// router location state; it is never persisted or sent except as ciphertext.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { api } from '../../api';
import type { EnrollSessionReceipt } from '../../api';
import {
  fingerprintCodec,
  type Fingerprint,
  type PublicKeyJwk,
} from '../../crypto';
import type { Principal, RegisteredKey, SessionMember } from '../../../../shared/types';
import { CodeBlock, ConfirmDialog, EmptyState, FeedbackMessage } from '../ui';
import { Icon } from '../ui/Icon';
import { ShieldBanner } from './ShieldBanner';
import { VaultSafe } from '../ceremony/VaultSafe';
import { buildSealRequest, newRecordId, type SealMember } from './seal';
import type { SecretDraft } from './NewSecretWizard';

/** True when the viewer asked the OS to minimise motion (motion-spec §4 off-switch). */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Roster row enriched with the joiner's resolved safety number + public key. */
interface RosterEntry {
  member: SessionMember;
  fingerprint: Fingerprint | null;
  publicKey: PublicKeyJwk | null;
  /** True while the attestation/key is still resolving. */
  resolving: boolean;
}

type SealPhase = 'idle' | 'splitting' | 'encrypting' | 'delivering' | 'done';

export interface InitiatorFlowProps {
  principal: Principal;
}

export function InitiatorFlow({ principal }: InitiatorFlowProps): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ sessionId?: string }>();

  // The plaintext-bearing draft handed from the wizard (in-memory only, R11).
  const draft = (location.state as { draft?: SecretDraft } | null)?.draft ?? null;

  const [session, setSession] = useState<EnrollSessionReceipt | null>(null);
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmSeal, setConfirmSeal] = useState(false);
  const [sealPhase, setSealPhase] = useState<SealPhase>('idle');
  const [receipt, setReceipt] = useState<{ n: number; k: number } | null>(null);
  // Locking-ceremony progress: how many of the K bolts have driven home so far.
  // Steps 0 → K one-per-bolt as the safe animates SHUT (the mirror of unseal's
  // opening); jumps straight to K under reduced motion. `lockComplete` gates the
  // door's final swing-shut + the receipt so the door fires once (motion-spec §3.3).
  const [lockedBolts, setLockedBolts] = useState(0);
  const [lockComplete, setLockComplete] = useState(false);

  const fpCache = useRef<Map<string, RegisteredKey>>(new Map());

  // ---- Open the session once -------------------------------------------------
  useEffect(() => {
    if (!draft || session) return;
    let cancelled = false;
    void (async () => {
      try {
        const created = await api.createEnrollSession();
        if (!cancelled) setSession(created);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error
              ? e.message
              : 'Something went wrong setting up the lobby. Your secret hasn’t been touched.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft, session]);

  // ---- Poll the roster (live; no socket dependency) --------------------------
  const sessionId = session?.id ?? params.sessionId ?? '';
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    async function tick(): Promise<void> {
      try {
        const members = await api.sessionRoster(sessionId);
        if (cancelled) return;
        // Resolve each joiner's safety number from the registry binding once.
        const next: RosterEntry[] = await Promise.all(
          members.map(async (member) => {
            const existing = roster.find((r) => r.member.pubkeyId === member.pubkeyId);
            if (existing && existing.fingerprint) {
              return { ...existing, member };
            }
            let key = fpCache.current.get(member.pubkeyId) ?? null;
            try {
              if (!key) {
                key = await api.getKey(member.pubkeyId);
                fpCache.current.set(member.pubkeyId, key);
              }
              const publicKey = JSON.parse(key.publicKeyJwk) as PublicKeyJwk;
              const fingerprint = await fingerprintCodec.derive(publicKey);
              return { member, fingerprint, publicKey, resolving: false };
            } catch {
              return { member, fingerprint: null, publicKey: null, resolving: true };
            }
          }),
        );
        if (!cancelled) setRoster(next);
      } catch {
        // A single poll tick failing (transient network) must not tear down the
        // lobby — the next tick recovers and the join code + seal path stay live.
        // A genuine session-create failure is surfaced above; per-tick blips are
        // intentionally quiet so the roster doesn't flicker an error every 3s.
      }
    }

    void tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // roster is intentionally excluded — tick reads the latest via closure each call
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const admittedCount = roster.filter((r) => r.member.state === 'admitted').length;
  const needed = draft?.totalN ?? 0;
  const canSeal = needed > 0 && admittedCount >= needed && !!draft;

  // ---- Admit / reject --------------------------------------------------------
  const admit = useCallback(
    async (pubkeyId: string) => {
      if (!sessionId) return;
      try {
        const updated = await api.admitJoiner(sessionId, { pubkeyId });
        setRoster((cur) =>
          cur.map((r) => (r.member.pubkeyId === pubkeyId ? { ...r, member: updated } : r)),
        );
      } catch (e) {
        setError(
          e instanceof Error ? `Couldn’t admit: ${e.message}` : 'The admit didn’t go through.',
        );
      }
    },
    [sessionId],
  );

  const reject = useCallback(
    async (pubkeyId: string) => {
      if (!sessionId) return;
      try {
        const updated = await api.rejectJoiner(sessionId, { pubkeyId });
        setRoster((cur) =>
          cur.map((r) => (r.member.pubkeyId === pubkeyId ? { ...r, member: updated } : r)),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'The reject didn’t go through.');
      }
    },
    [sessionId],
  );

  // ---- Seal (CLIENT-SIDE) ----------------------------------------------------
  const runSeal = useCallback(async () => {
    if (!draft || !sessionId) return;
    setConfirmSeal(false);
    setError(null);
    setSealPhase('splitting');
    try {
      const members: SealMember[] = roster
        .filter((r) => r.member.state === 'admitted' && r.publicKey)
        .map((r) => ({
          pubkeyId: r.member.pubkeyId,
          email: r.member.email,
          publicKey: r.publicKey as PublicKeyJwk,
        }));

      // buildSealRequest performs the envelope + Shamir split + per-recipient
      // ECDH encryption entirely in-browser. The data key and raw shares are
      // generated and discarded inside; only opaque ciphertext is returned (R11).
      setSealPhase('encrypting');
      const body = await buildSealRequest(sessionId, draft.material, members, {
        recordId: newRecordId(),
        description: draft.description || draft.name,
        sensitivity: draft.sensitivity,
        ownerId: principal.id,
        thresholdK: draft.thresholdK,
        timeoutSeconds: draft.timeoutSeconds,
      });

      setSealPhase('delivering');
      const sealed = await api.sealRecord(body);
      setReceipt({ n: sealed.n, k: sealed.k });
      setSealPhase('done');
    } catch (e) {
      setSealPhase('idle');
      setError(
        e instanceof Error
          ? `Sealing didn’t complete: ${e.message}`
          : 'The split was interrupted, so nothing was distributed. Your secret is still here — try sealing again.',
      );
    }
  }, [draft, sessionId, roster, principal.id]);

  // ---- Locking ceremony (the SEAL-complete animation) ------------------------
  // Once the seal POST returns, drive the bolts home one-by-one (the mirror of the
  // unseal opening). Each step advances on a --dur-ceremony-bolt cadence; the door
  // swings shut after the last bolt. Under reduced motion we skip the staging and
  // land on the fully-locked state instantly (motion-spec §4 — the stepped meter +
  // assertive copy still convey the outcome).
  useEffect(() => {
    if (sealPhase !== 'done' || !receipt) return;
    const k = receipt.k;
    if (prefersReducedMotion()) {
      setLockedBolts(k);
      setLockComplete(true);
      return;
    }
    setLockedBolts(0);
    setLockComplete(false);
    let i = 0;
    // ~600ms per bolt mirrors --dur-ceremony-bolt; the door swings ~400ms after the
    // final bolt seats (it is a CSS transition keyed on the locked state class).
    const stepMs = 600;
    const id = window.setInterval(() => {
      i += 1;
      setLockedBolts(i);
      if (i >= k) {
        window.clearInterval(id);
        window.setTimeout(() => setLockComplete(true), 400);
      }
    }, stepMs);
    return () => window.clearInterval(id);
  }, [sealPhase, receipt]);

  // ---- No-draft guard --------------------------------------------------------
  if (!draft) {
    return (
      <div className="bgEnroll">
        <EmptyState
          title="Start from a new secret"
          subtitle="Open the new-secret wizard from the Vault to convene a sealing session."
        />
        <div style={{ textAlign: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
            Back to the Vault
          </button>
        </div>
      </div>
    );
  }

  const expiresInMs = session ? session.expiresAt - Date.now() : 0;
  const expiresLabel = formatCountdown(expiresInMs);

  return (
    <div className="bgEnroll">
      <ShieldBanner />

      <div className="bgLobbyGrid">
        {/* Join code */}
        <div className="bgPanel">
          <div className="bgPanelBody">
            <div className="kicker">Join code</div>
            {session ? (
              <>
                <div className="bgJoinCode">
                  <div>
                    <div className="bgJoinWords">{session.joinWords}</div>
                    <div className="bgJoinPin">PIN {session.pin}</div>
                  </div>
                </div>
                <CodeBlock code={`${session.joinWords} · ${session.pin}`} />
                <div className="bgJoinMeta">
                  Single-session · expires in {expiresLabel} · rate-limited
                </div>
                <p className="bgShieldNote" style={{ marginTop: 0 }}>
                  Share this code with the people who will each hold one shard. The code grants
                  lobby entry only — you admit each joiner after verifying their safety number.
                </p>
              </>
            ) : (
              <div role="status" aria-live="polite" className="bgJoinMeta">
                Creating a join code…
              </div>
            )}
          </div>
        </div>

        {/* Live roster */}
        <div className="bgPanel">
          <div className="bgPanelBody">
            <div className="bgRosterHead">
              <div className="kicker">Live roster</div>
              <span className="bgRosterCount">
                {admittedCount} / {needed} admitted
              </span>
            </div>
            {roster.length === 0 ? (
              <EmptyState
                title="No joiners yet"
                subtitle="Share the code to begin. Joiners appear here once they publish a key."
              />
            ) : (
              <div className="bgRoster">
                {roster.map((r) => (
                  <RosterRow key={r.member.pubkeyId} entry={r} onAdmit={admit} onReject={reject} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <FeedbackMessage ok={false} msg={error} />}

      <div className="bgFootBar">
        <span className="bgFootStatus">
          {canSeal
            ? `${admittedCount} of ${needed} admitted — ready to seal.`
            : `Admit ${needed} ${needed === 1 ? 'member' : 'members'} before you can seal.`}
        </span>
        <div className="bgFootActions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate('/')}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSeal || sealPhase !== 'idle'}
            onClick={() => setConfirmSeal(true)}
          >
            Seal secret
          </button>
        </div>
      </div>

      {/* Consequence dialog (content guide §5.7) */}
      {confirmSeal && (
        <ConfirmDialog
          title={`Seal ${draft.name}`}
          confirmLabel="Seal secret"
          onConfirm={runSeal}
          onCancel={() => setConfirmSeal(false)}
          message={
            <>
              You’re about to split <strong>{draft.name}</strong> across {needed} members, needing{' '}
              {draft.thresholdK} to unseal. This is the only moment the secret exists in plain text
              here — after sealing, even you can’t read it without a quorum.
            </>
          }
        />
      )}

      {/* Stepped seal progress (modal, non-dismissable) */}
      {sealPhase !== 'idle' && sealPhase !== 'done' && (
        <div className="bgPanel" role="status" aria-live="polite" style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'grid', placeItems: 'center', background: 'var(--color-overlay-bg)' }}>
          <div className="bgPanelBody" style={{ maxWidth: 420, background: 'var(--color-bg2)', border: '1px solid var(--color-border-modal)', borderRadius: 'var(--radius-lg)' }}>
            <h2 style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>Sealing {draft.name}</h2>
            <ul className="bgSealSteps">
              <SealStep label="Splitting the data key" active={sealPhase === 'splitting'} done={sealPhase === 'encrypting' || sealPhase === 'delivering'} />
              <SealStep label="Encrypting one shard per member" active={sealPhase === 'encrypting'} done={sealPhase === 'delivering'} />
              <SealStep label="Delivering shards (store-and-forward)" active={sealPhase === 'delivering'} done={false} />
            </ul>
            <p className="bgShieldNote" style={{ marginTop: 0 }}>No server ever sees the plaintext.</p>
          </div>
        </div>
      )}

      {/* Locking ceremony — the safe drives SHUT as the seal completes (the mirror
          of the unseal opening). The door + bolts are token-driven CSS; the bolt
          count steps via the effect above. The receipt CTA appears once locked. */}
      {sealPhase === 'done' && receipt && (
        <div
          className="bgLockCeremony"
          role="dialog"
          aria-modal="true"
          aria-label={`Sealing ${draft.name}`}
        >
          <div className="bgLockCeremonyCard">
            <div
              aria-live="assertive"
              className="bgSafeMeterVisual"
            >
              {lockComplete
                ? `${draft.name} is sealed — split across ${receipt.n} members, ${receipt.k} needed to unseal.`
                : 'Sealing — driving the bolts home.'}
            </div>

            <h2 className="bgLockCeremonyTitle">
              {lockComplete ? 'Sealed' : `Sealing ${draft.name}`}
            </h2>

            <div className="bgLockSafeStage">
              <VaultSafe
                mode="seal"
                submitted={lockedBolts}
                threshold={receipt.k}
                state={lockComplete ? 'locked' : 'sealing'}
              />
            </div>

            {lockComplete ? (
              <>
                <p className="bgLockCeremonyBody">
                  <strong>{draft.name}</strong> is split across {receipt.n} members — {receipt.k}{' '}
                  are needed to unseal. The plaintext is wiped from this browser; recovering it now
                  needs a quorum. No material was logged.
                </p>
                <div className="bgLockCeremonyActions">
                  <button type="button" className="btn btn-primary" onClick={() => navigate('/')}>
                    Done
                  </button>
                </div>
              </>
            ) : (
              <p className="bgLockCeremonyBody" role="status">
                Sealed shards are being locked away — {lockedBolts} of {receipt.k} bolts thrown.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SealStep({ label, active, done }: { label: string; active: boolean; done: boolean }): React.JSX.Element {
  return (
    <li className={`bgSealStep ${active ? 'bgSealStepActive' : ''} ${done ? 'bgSealStepDone' : ''}`}>
      <span className="bgSealStepMark" aria-hidden="true">
        {done ? <Icon icon={Check} size="xs" /> : active ? <span className="spinner" /> : '○'}
      </span>
      {label}
    </li>
  );
}

function RosterRow({
  entry,
  onAdmit,
  onReject,
}: {
  entry: RosterEntry;
  onAdmit: (pubkeyId: string) => void;
  onReject: (pubkeyId: string) => void;
}): React.JSX.Element {
  const { member, fingerprint, resolving } = entry;
  const admitted = member.state === 'admitted';
  const rejected = member.state === 'rejected';
  return (
    <div className={`bgRosterRow ${admitted ? 'bgRosterAdmitted' : ''} ${rejected ? 'bgRosterDanger' : ''}`}>
      <div className="bgRosterTop">
        <span className="bgRosterEmail">{member.email}</span>
        <span className="bgJoinMeta">{member.authMethod}</span>
      </div>
      <div className="bgRosterFp">
        {resolving
          ? 'verifying key…'
          : fingerprint
            ? `safety number: ${fingerprint.words.join(' · ')}`
            : 'safety number unavailable'}
      </div>
      {admitted ? (
        <span className="badge badge-success" style={{ justifySelf: 'start' }}>admitted</span>
      ) : rejected ? (
        <span className="badge badge-danger" style={{ justifySelf: 'start' }}>rejected</span>
      ) : (
        <div className="bgRosterActions">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={resolving || !fingerprint}
            onClick={() => onAdmit(member.pubkeyId)}
          >
            Admit
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onReject(member.pubkeyId)}
          >
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

/** mm:ss countdown label; clamps at zero. */
function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

export default InitiatorFlow;
